import { canvas, drawSpriteRect, drawTriangles } from "./gl.js";
import { normalizedToPixel } from "./layout.js";
import { clamp, lerp } from "./utils.js";
import {
  appState,
  SCORE_BAG_BURST_DURATION,
  SCORE_BAG_FLY_DURATION,
  SCORE_BAG_MAX,
  SCORE_BAG_SIZE,
  luckyBagSprite,
  luckyBagBlueSprite,
  scoreBags,
  scoreBursts,
  scoreEvents,
} from "./state.js";
import { addBlueScore, addScore, getScoreAverage } from "./score.js";
import { playSfx } from "./sfx.js";
import { applyEconomyDelta } from "./backend.js";

const BAG_BURST_RADIUS = 0.06;
const BAG_TARGET_SPREAD = 0.02;
const BAG_DELAY_MAX = 0.12;
const BURST_DURATION = 0.5;
const BURST_RING_DURATION = 0.4;
const BURST_RING_MAX = 0.11;
const BURST_PARTICLE_COUNT = 6;
const BURST_PARTICLE_SPEED = 0.22;
const BURST_PARTICLE_SIZE = 0.03;
const BURST_PARTICLE_SPIN = Math.PI * 2.2;
const BAG_IMPACT_DURATION = 0.2;
const RING_SEGMENTS = 24;
const MAX_BURST_PARTICLES = 160;
const ringAngles = new Array(RING_SEGMENTS + 1).fill(0).map((_, i) => {
  const angle = (i / RING_SEGMENTS) * Math.PI * 2;
  return { cos: Math.cos(angle), sin: Math.sin(angle) };
});
const zeroTexcoordsCache = new Map();
let nextScoreEventId = 1;
const scoreEventTotals = new Map();

export function queueScoreEvent(amount, origin, options = null) {
  const value = Number(amount);
  if (!Number.isFinite(value) || value <= 0) {
    return;
  }
  const start = origin || { x: 0.5, y: 0.5 };
  const applyScore = options?.applyScore !== false;
  scoreEvents.push({ amount: value, origin: start, options: options || null, applyScore });
  if (!options || options.rewardSfx !== false) {
    playSfx("bag_spawn");
  }
  if (!options || options.sync !== false) {
    const targetKey = options && options.targetKey === "blue" ? "blue" : "red";
    const delta = targetKey === "blue" ? { blue_bag: value } : { red_bag: value };
    applyEconomyDelta(delta, { applySnapshot: false }).catch(() => {});
  }
  appState.requestDraw?.();
}

export function clearScoreBags() {
  scoreEvents.length = 0;
  scoreBags.length = 0;
  scoreBursts.length = 0;
  scoreEventTotals.clear();
}

export function updateScoreBags(dt, panelRect, targetNormalized) {
  if (!panelRect) {
    return;
  }
  let redTarget = targetNormalized;
  let blueTarget = null;
  if (targetNormalized && typeof targetNormalized === "object" && "red" in targetNormalized) {
    redTarget = targetNormalized.red;
    blueTarget = targetNormalized.blue;
  }
  if (!redTarget) {
    redTarget = { x: 0.1, y: 0.9 };
  }
  if (!blueTarget) {
    blueTarget = redTarget;
  }
  while (scoreEvents.length) {
    const event = scoreEvents.shift();
    if (event) {
      const targetKey = event.options?.targetKey === "blue" ? "blue" : "red";
      const target = targetKey === "blue" ? blueTarget : redTarget;
      const sprite =
        event.options?.spriteKey === "blue" ? luckyBagBlueSprite : luckyBagSprite;
      spawnScoreBags(
        event.amount,
        event.origin,
        target,
        event.options,
        sprite,
        targetKey,
        event.applyScore
      );
    }
  }
  updateScoreBursts(dt);
  if (!scoreBags.length) {
    return;
  }
  for (let i = scoreBags.length - 1; i >= 0; i -= 1) {
    const bag = scoreBags[i];
    if (bag.delay > 0) {
      bag.delay = Math.max(0, bag.delay - dt);
      if (bag.delay > 0) {
        continue;
      }
    }
    bag.elapsed += dt;
    if (bag.phase === "burst") {
      const t = clamp(bag.elapsed / bag.burstDuration, 0, 1);
      const eased = easeOut(t);
      bag.pos = lerpPoint(bag.burstFrom, bag.burstTo, eased);
      if (t >= 1) {
        bag.phase = "fly";
        bag.elapsed = 0;
        bag.flyFrom = { ...bag.burstTo };
        bag.flyTo = { ...bag.target };
      }
      continue;
    }
    if (bag.phase === "fly") {
      const t = clamp(bag.elapsed / bag.flyDuration, 0, 1);
      const eased = easeInOut(t);
      const base = lerpPoint(bag.flyFrom, bag.flyTo, eased);
      base.y += Math.sin(t * Math.PI) * bag.arc;
      bag.pos = base;
      if (t >= 1) {
        bag.phase = "impact";
        bag.elapsed = 0;
        bag.pos = { ...bag.target };
        playSfx("bag_arrive");
        spawnScoreBurst(bag.target);
      }
    } else if (bag.phase === "impact") {
      if (!bag.pos) {
        bag.pos = { ...bag.target };
      }
      const t = clamp(bag.elapsed / BAG_IMPACT_DURATION, 0, 1);
      if (t >= 1) {
        if (bag.applyScore !== false) {
          if (bag.targetKey === "blue") {
            addBlueScore(bag.value);
          } else {
            addScore(bag.value);
          }
        }
        if (bag.eventId != null) {
          const entry = scoreEventTotals.get(bag.eventId);
          if (entry) {
            entry.remaining -= 1;
            if (entry.remaining <= 0) {
              scoreEventTotals.delete(bag.eventId);
            }
          }
        }
        scoreBags.splice(i, 1);
      }
    }
  }
}

export function renderScoreBags(panelRect) {
  if (!panelRect || !scoreBags.length) {
    if (panelRect && scoreBursts.length) {
      renderScoreBursts(panelRect);
    }
    return;
  }
  renderScoreBursts(panelRect);
  const baseSize = panelRect.height * SCORE_BAG_SIZE;
  for (const bag of scoreBags) {
    if (!bag.pos) {
      continue;
    }
    const center = normalizedToPixel(bag.pos, panelRect);
    const impactT = bag.phase === "impact" ? clamp(bag.elapsed / BAG_IMPACT_DURATION, 0, 1) : 0;
    const shrink = bag.phase === "impact" ? 1 - impactT : 1;
    const size = baseSize * bag.scale * shrink;
    const rect = {
      left: center.x - size * 0.5,
      bottom: center.y - size * 0.5,
      width: size,
      height: size,
    };
    const alpha = bag.phase === "impact" ? 1 - impactT : 1;
    const color = bag.phase === "impact" ? [1, 1, 1, alpha] : [1, 1, 1, 1];
    const sprite = bag.sprite && bag.sprite.texture ? bag.sprite : luckyBagSprite;
    if (sprite && sprite.texture) {
      drawSpriteRect(sprite, rect, color);
    }
  }
}

function spawnScoreBags(amount, origin, targetNormalized, options, sprite, targetKey, applyScore) {
  const total = Math.max(1, Math.round(amount));
  const average = getScoreAverage();
  const safeAverage = average > 0 ? average : total;
  const targetCount = Math.round((total / safeAverage) * 3);
  const count = Math.min(
    Math.max(1, Math.min(targetCount, SCORE_BAG_MAX)),
    total
  );
  const eventId = nextScoreEventId++;
  scoreEventTotals.set(eventId, { remaining: count });
  const values = splitScore(total, count);
  const burstScale = getOptionNumber(options, "burstScale", 1);
  const bagBurstScale = getOptionNumber(options, "bagBurstScale", burstScale);
  spawnScoreBurst(origin, burstScale);
  for (let i = 0; i < count; i += 1) {
    const angle = Math.random() * Math.PI * 2;
    const radius = BAG_BURST_RADIUS * bagBurstScale * (0.6 + Math.random() * 0.6);
    const burstTo = clampPoint({
      x: origin.x + Math.cos(angle) * radius,
      y: origin.y + Math.sin(angle) * radius,
    });
    const spread = BAG_TARGET_SPREAD * (0.5 + Math.random());
    const target = clampPoint({
      x: targetNormalized.x + (Math.random() * 2 - 1) * spread,
      y: targetNormalized.y + (Math.random() * 2 - 1) * spread,
    });
    scoreBags.push({
      eventId,
      value: values[i] || 0,
      phase: "burst",
      delay: i * 0.1,
      elapsed: 0,
      burstDuration: SCORE_BAG_BURST_DURATION,
      flyDuration: SCORE_BAG_FLY_DURATION,
      impactDuration: BAG_IMPACT_DURATION,
      burstFrom: { ...origin },
      burstTo,
      flyFrom: { ...origin },
      flyTo: { ...target },
      target,
      targetKey,
      arc: 0.04 + Math.random() * 0.05,
      scale: 0.9 + Math.random() * 0.35,
      pos: { ...origin },
      sprite,
      applyScore: applyScore !== false,
    });
  }
}

function splitScore(total, count) {
  if (count <= 1) {
    return [total];
  }
  const base = Math.floor(total / count);
  let remaining = total - base * count;
  const values = new Array(count).fill(base);
  for (let i = 0; i < values.length && remaining > 0; i += 1) {
    values[i] += 1;
    remaining -= 1;
  }
  return values;
}

function clampPoint(point) {
  return {
    x: clamp(point.x, 0.02, 0.98),
    y: clamp(point.y, 0.02, 0.98),
  };
}

function spawnScoreBurst(origin, scale = 1) {
  const settings = getBurstSettings(scale);
  const totalParticles = getTotalBurstParticles();
  const available = Math.max(0, MAX_BURST_PARTICLES - totalParticles);
  if (available <= 0) {
    return;
  }
  settings.count = Math.min(settings.count, available);
  const particles = [];
  for (let i = 0; i < settings.count; i += 1) {
    const angle = Math.random() * Math.PI * 2;
    const speed = settings.speed * (0.6 + Math.random() * 0.8);
    const size = settings.size * (0.6 + Math.random() * 0.7);
    const sides = 3 + Math.floor(Math.random() * 4);
    const basePoints = createPolygonPoints(sides);
    const positions = new Float32Array((sides - 2) * 3 * 2);
    particles.push({
      angle,
      speed,
      size,
      sides,
      spin: (Math.random() * 2 - 1) * settings.spin,
      hue: 40 + Math.random() * 25,
      basePoints,
      positions,
      texcoords: getZeroTexcoords(positions.length),
    });
  }
  const ringPositions = new Float32Array(RING_SEGMENTS * 6 * 2);
  scoreBursts.push({
    origin: origin || { x: 0.5, y: 0.5 },
    elapsed: 0,
    ringMax: settings.ringMax,
    ringPositions,
    ringTexcoords: getZeroTexcoords(ringPositions.length),
    particles,
  });
}

function getTotalBurstParticles() {
  let total = 0;
  for (const burst of scoreBursts) {
    if (burst?.particles) {
      total += burst.particles.length;
    }
  }
  return total;
}

function updateScoreBursts(dt) {
  if (!scoreBursts.length) {
    return;
  }
  for (let i = scoreBursts.length - 1; i >= 0; i -= 1) {
    const burst = scoreBursts[i];
    burst.elapsed += dt;
    if (burst.elapsed >= BURST_DURATION) {
      scoreBursts.splice(i, 1);
    }
  }
}

function renderScoreBursts(panelRect) {
  if (!scoreBursts.length) {
    return;
  }
  const base = Math.min(panelRect.width, panelRect.height);
  for (const burst of scoreBursts) {
    const t = clamp(burst.elapsed / BURST_DURATION, 0, 1);
    const ringT = clamp(burst.elapsed / BURST_RING_DURATION, 0, 1);
    const ringAlpha = 1 - ringT;
    const ringRadius = burst.ringMax * (0.3 + ringT);
    drawRing(panelRect, burst, ringRadius, ringRadius * 0.55, [1, 0.6, 0.2, ringAlpha * 0.6]);

    for (const particle of burst.particles) {
      const travel = particle.speed * t;
      const pos = {
        x: burst.origin.x + Math.cos(particle.angle) * travel,
        y: burst.origin.y + Math.sin(particle.angle) * travel,
      };
      const size = particle.size * (1 - t * 0.4);
      const alpha = (1 - t) * 0.85;
      drawPolygonParticle(panelRect, pos, size * base, particle, particle.spin * t, [
        1,
        0.45,
        0.2,
        alpha,
      ]);
    }
  }
}

function drawRing(panelRect, burst, outer, inner, color) {
  const center = normalizedToPixel(burst.origin, panelRect);
  const outerRadius = outer * Math.min(panelRect.width, panelRect.height);
  const innerRadius = inner * Math.min(panelRect.width, panelRect.height);
  const positions = burst.ringPositions;
  let idx = 0;
  for (let i = 0; i < RING_SEGMENTS; i += 1) {
    const a0 = ringAngles[i];
    const a1 = ringAngles[i + 1];
    const p0x = center.x + a0.cos * outerRadius;
    const p0y = center.y + a0.sin * outerRadius;
    const p1x = center.x + a1.cos * outerRadius;
    const p1y = center.y + a1.sin * outerRadius;
    const p2x = center.x + a1.cos * innerRadius;
    const p2y = center.y + a1.sin * innerRadius;
    const p3x = center.x + a0.cos * innerRadius;
    const p3y = center.y + a0.sin * innerRadius;
    idx = pushTriangleInto(positions, idx, p0x, p0y, p1x, p1y, p2x, p2y);
    idx = pushTriangleInto(positions, idx, p0x, p0y, p2x, p2y, p3x, p3y);
  }
  drawTriangles(positions, burst.ringTexcoords, color);
}

function drawPolygonParticle(panelRect, origin, size, particle, rotation, color) {
  const center = normalizedToPixel(origin, panelRect);
  const cos = Math.cos(rotation);
  const sin = Math.sin(rotation);
  const base = particle.basePoints;
  const positions = particle.positions;
  const pointCount = base.length / 2;
  const points = particle.points || new Float32Array(base.length);
  for (let i = 0; i < base.length; i += 2) {
    const x = base[i] * size;
    const y = base[i + 1] * size;
    points[i] = x * cos - y * sin + center.x;
    points[i + 1] = x * sin + y * cos + center.y;
  }
  particle.points = points;
  let idx = 0;
  for (let i = 1; i < pointCount - 1; i += 1) {
    idx = pushTriangleInto(
      positions,
      idx,
      points[0],
      points[1],
      points[i * 2],
      points[i * 2 + 1],
      points[(i + 1) * 2],
      points[(i + 1) * 2 + 1]
    );
  }
  drawTriangles(positions, particle.texcoords, color);
}

function pushTriangleInto(positions, idx, x0, y0, x1, y1, x2, y2) {
  positions[idx++] = toNdcX(x0);
  positions[idx++] = toNdcY(y0);
  positions[idx++] = toNdcX(x1);
  positions[idx++] = toNdcY(y1);
  positions[idx++] = toNdcX(x2);
  positions[idx++] = toNdcY(y2);
  return idx;
}

function toNdcX(x) {
  return (x / canvas.width) * 2 - 1;
}

function toNdcY(y) {
  return (y / canvas.height) * 2 - 1;
}

function getBurstSettings(scale) {
  const s = Math.max(0.5, scale);
  return {
    count: Math.min(18, Math.round(BURST_PARTICLE_COUNT * (0.8 + s * 0.8))),
    speed: BURST_PARTICLE_SPEED * (0.7 + s * 0.8),
    size: BURST_PARTICLE_SIZE * (0.7 + s * 0.5),
    spin: BURST_PARTICLE_SPIN * (0.8 + s * 0.6),
    ringMax: BURST_RING_MAX * (0.8 + s * 0.9),
  };
}

function getOptionNumber(options, key, fallback) {
  if (!options) {
    return fallback;
  }
  const value = Number(options[key]);
  return Number.isFinite(value) ? value : fallback;
}

function createPolygonPoints(sides) {
  const count = Math.max(3, sides);
  const angleStep = (Math.PI * 2) / count;
  const points = new Float32Array(count * 2);
  for (let i = 0; i < count; i += 1) {
    const angle = i * angleStep;
    points[i * 2] = Math.cos(angle);
    points[i * 2 + 1] = Math.sin(angle);
  }
  return points;
}

function getZeroTexcoords(length) {
  if (zeroTexcoordsCache.has(length)) {
    return zeroTexcoordsCache.get(length);
  }
  const array = new Float32Array(length);
  zeroTexcoordsCache.set(length, array);
  return array;
}

function lerpPoint(a, b, t) {
  return {
    x: lerp(a.x, b.x, t),
    y: lerp(a.y, b.y, t),
  };
}

function easeOut(t) {
  return 1 - Math.pow(1 - t, 3);
}

function easeInOut(t) {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
}
